import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Camera } from "lucide-react";
import { fileToDownscaledBase64 } from "@/lib/img";
import GoogleSignInButton from "@/components/GoogleSignInButton";

function fileToBase64(file) {
  return fileToDownscaledBase64(file, 512, 0.85);
}

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", profile_pic: "" });
  const [busy, setBusy] = useState(false);

  const onPic = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    const b64 = await fileToBase64(f);
    setForm({ ...form, profile_pic: b64 });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created. Namaste!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2 font-display text-2xl"><Leaf size={20}/> Immortal Vedas</div>
        <div>
          <h2 className="font-display text-5xl italic leading-tight mb-4">Begin a journey<br/>toward balance.</h2>
          <p className="opacity-70 max-w-md">Create an account to book consultations, study the Samhitas, order remedies, and chat with the Vaidya.</p>
        </div>
        <div className="text-xs opacity-60">© Immortal Vedas</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="overline mb-2">Create account</div>
          <h1 className="font-display text-4xl mb-8">Join the practice</h1>

          <GoogleSignInButton label="Sign up with Google" testid="google-signup-btn" />
          <div className="flex items-center gap-3 py-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">or with email</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="flex justify-center mb-6">
            <label className="cursor-pointer relative" data-testid="register-pic-label">
              <div className="w-24 h-24 rounded-full border-2 border-dashed border-border bg-muted overflow-hidden flex items-center justify-center">
                {form.profile_pic
                  ? <img src={form.profile_pic} alt="" className="w-full h-full object-cover" />
                  : <Camera className="text-muted-foreground" />}
              </div>
              <input type="file" accept="image/*" onChange={onPic} className="hidden" data-testid="register-pic-input" />
              <span className="block text-xs text-center mt-2 text-secondary">Upload photo</span>
            </label>
          </div>

          <div className="space-y-3">
            <div><Label>Full Name</Label>
              <Input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="register-name" className="bg-transparent"/></div>
            <div><Label>Email</Label>
              <Input type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="register-email" className="bg-transparent"/></div>
            <div><Label>Phone</Label>
              <Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="register-phone" className="bg-transparent"/></div>
            <div><Label>Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} data-testid="register-password" className="bg-transparent"/></div>
            <Button type="submit" disabled={busy} className="w-full btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="register-submit">
              {busy ? "Creating…" : "Create account"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Already a patient? <Link to="/login" className="text-primary underline" data-testid="link-login">Sign in</Link>
            </p>
          </div>
        </form>
      </div>
    </main>
  );
}
